import React, { useEffect, useRef } from "react";
// ANT DESIGN COMPONENTS
import { Table, notification } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateTerminalAutoScroll } from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import { useBankDispatch } from "../socketMessageState/BankMessagesContext";
import { subscribeWebSocketMessage } from "../socketMessageState/webSocketMessageBus";
import { subscribeUiCmdTx } from "../socketMessageState/uiCmdTxBus";
import { txFrameToTerminalRow } from "../../../flatbuffers/decoders/uiCmdDecoder";
// UTILS
import { getTableRowClass, generateExcelAndDownload } from "../../../utils/utils";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import getFaultDisplayName from "../../../utils/faultDisplay";
import { directionDisplayName } from "../../../flatbuffers/types/enumDisplayUtils";
// INTERFACES
import { TerminalExcelReportInterface } from "../../../interfaces/EdgeManagement.interface";
import {
  SocketMessageInterface,
  TerminalFilterType,
  TerminalRowInterface,
} from "../../../interfaces/Monitoring.interface";
import { ReplayRecord } from "../../../utils/binaryReplayCodec";
import { uuidToString } from "../../../utils/uuid";
import { RootState } from "../../../app/store";
// COMPONENTS
import TerminalTableColumns from "./TerminalTableColumns";

const MAX_TERMINAL_MESSAGES = 1000;

/**
 * Coalescing key for a socket message. Two messages with the same key
 * represent the same logical event from the controller (same lift, same
 * encoded type + message type, same inner code, same position) — typically
 * a rapid `active=true` → `active=false` pulse. The Terminal table
 * replaces a previous row sharing this key instead of appending so the
 * operator sees the LATEST `Msg Active` state per unique event, not a
 * flickering pair of rows alternating green/red Tags.
 *
 * The inner code is best-effort — for nested FlatBuffer Object API trees
 * we walk down to the deepest `message` numeric field.
 */
function extractInnerMessageCode(content: unknown): number | null {
  let cur: any = content;
  for (let i = 0; i < 6; i += 1) {
    if (cur == null || typeof cur !== "object") return null;
    if (typeof cur.message === "number") return cur.message;
    if (cur.message && typeof cur.message === "object") {
      cur = cur.message;
    } else {
      let next: any = null;
      const keys = Object.keys(cur);
      for (let k = 0; k < keys.length; k += 1) {
        const v = cur[keys[k]];
        if (v && typeof v === "object") {
          next = v;
          break;
        }
      }
      if (next === null) return null;
      cur = next;
    }
  }
  return null;
}

export function coalesceKey(msg: TerminalRowInterface): string {
  // Outbound rows are discrete operator actions, not a state stream: pressing
  // the same button twice is two events worth seeing, and an outbound row must
  // never merge into an inbound one that happens to describe the same floor.
  // The bus-assigned `seq` makes each TX key unique, which both prevents
  // coalescing and gives Antd a stable, collision-free rowKey.
  if (msg.txMeta) return `TX|${msg.txMeta.seq}`;
  const liftKey = uuidToString(msg.liftId) || "-";
  const ct = msg.msg?.contentType ?? "-";
  const mt = msg.msg?.messageType ?? "-";
  const inner = extractInnerMessageCode(msg.msg?.content);
  const pos = msg.position ?? "-";
  return `${liftKey}|${ct}|${mt}|${inner ?? "-"}|${pos}`;
}

/**
 * Merge a batch of incoming messages into an accumulator, replacing any
 * previous row that shares a coalesce key. Order of distinct rows is
 * preserved (by first-seen index); coalesced rows update in place.
 *
 * Exported for test coverage.
 */
export function coalesceMessages(
  prev: TerminalRowInterface[],
  incoming: TerminalRowInterface[],
): TerminalRowInterface[] {
  if (incoming.length === 0) return prev;
  const out = prev.slice();
  const indexByKey = new Map<string, number>();
  for (let i = 0; i < out.length; i += 1) {
    indexByKey.set(coalesceKey(out[i]), i);
  }
  for (let i = 0; i < incoming.length; i += 1) {
    const m = incoming[i];
    const key = coalesceKey(m);
    const existing = indexByKey.get(key);
    if (existing !== undefined) {
      out[existing] = m;
    } else {
      indexByKey.set(key, out.length);
      out.push(m);
    }
  }
  return out;
}

/**
 * JSON for the clipboard, tolerant of the bigints a row is full of.
 *
 * Every row carries `UuidT` values whose `U128T` halves are bigints, and TX
 * rows add the UiCmd `timestamp` on top. `JSON.stringify` throws outright on a
 * bigint, so the plain call this replaces threw synchronously out of the click
 * handler — past the `.catch()` that was meant to report it — and copying a row
 * simply did nothing. Serialising bigints as decimal strings keeps every digit.
 */
export function stringifyRow(record: TerminalRowInterface): string {
  return JSON.stringify(record, (_key, value) =>
    typeof value === "bigint" ? value.toString() : value,
  );
}

export default function TerminalTable({
  bankId,
  liftId,
  socketMessages,
  setSocketMessages,
  filterType,
  liftNameMap = {},
  capturedMessagesRef,
}: {
  bankId: string;
  liftId: string;
  socketMessages: TerminalRowInterface[];
  setSocketMessages: (newValue: TerminalRowInterface[]) => void;
  filterType: TerminalFilterType;
  liftNameMap?: Record<string, string>;
  capturedMessagesRef: React.MutableRefObject<ReplayRecord[]>;
}) {
  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const {
    terminalResetTable,
    terminalDownloadReport,
    terminalAutoScroll,
    terminalShowCarHallCalls,
    terminalIconMode,
    stopSocketConnection,
    binaryCaptureActive,
    terminalShowTxMessages,
  } = useSelector((state: RootState) => state.monitoring);

  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const columns = React.useMemo(
    () =>
      TerminalTableColumns({
        terminalShowCarHallCalls,
        liftNameMap,
        terminalIconMode,
      }),
    [terminalShowCarHallCalls, liftNameMap, terminalIconMode],
  );
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const dispatchSocketMessage = useBankDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const launchNotification = () => {
    notification.success({
      message: "Text copied to clipboard",
      placement: "top",
      className: `monitoring__notification__${theme}`,
    });
    dispatch(updateTerminalAutoScroll(false));
  };

  // ************************************************ */
  // STREAM SUBSCRIPTION REFS ************************ */
  // Refs so the bus listener (registered once) reads current values without
  // being re-attached on every prop/state change — avoids dropping messages
  // during the unsubscribe/subscribe gap.
  const filterTypeRef = useRef(filterType);
  filterTypeRef.current = filterType;
  const liftIdRef = useRef(liftId);
  liftIdRef.current = liftId;
  const bankIdRef = useRef(bankId);
  bankIdRef.current = bankId;
  const stopSocketConnectionRef = useRef(stopSocketConnection);
  stopSocketConnectionRef.current = stopSocketConnection;
  const terminalAutoScrollRef = useRef(terminalAutoScroll);
  terminalAutoScrollRef.current = terminalAutoScroll;
  const binaryCaptureActiveRef = useRef(binaryCaptureActive);
  binaryCaptureActiveRef.current = binaryCaptureActive;
  const terminalShowTxMessagesRef = useRef(terminalShowTxMessages);
  terminalShowTxMessagesRef.current = terminalShowTxMessages;

  // Internal accumulator that mirrors the displayed rows. We can't read
  // `socketMessages` inside the bus listener (parent state isn't updated
  // until React re-renders), so we keep our own append-only buffer and
  // hand a fresh array to setSocketMessages on each RAF flush.
  const accumulatedRef = useRef<TerminalRowInterface[]>([]);
  const pendingMessagesRef = useRef<TerminalRowInterface[]>([]);
  const flushScheduledRef = useRef(false);
  const unmountedRef = useRef(false);

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (terminalResetTable) {
      setSocketMessages([]);
      accumulatedRef.current = [];
      pendingMessagesRef.current = [];
    }
  }, [terminalResetTable]);

  useEffect(() => {
    unmountedRef.current = false;

    const flush = () => {
      flushScheduledRef.current = false;
      if (unmountedRef.current) return;
      const pending = pendingMessagesRef.current;
      if (pending.length === 0) return;
      pendingMessagesRef.current = [];
      // Coalesce by (lift, contentType, messageType, innerCode, position)
      // so a controller pulsing active=true → false on the same logical
      // event collapses into a single row showing the LATEST state.
      const next = coalesceMessages(accumulatedRef.current, pending);
      const trimmed =
        next.length > MAX_TERMINAL_MESSAGES
          ? next.slice(next.length - MAX_TERMINAL_MESSAGES)
          : next;
      accumulatedRef.current = trimmed;
      setSocketMessages(trimmed);
      const scrollBody = tableContainerRef.current?.querySelector(".ant-table-body");
      if (scrollBody && terminalAutoScrollRef.current) {
        scrollBody.scrollTop = scrollBody.scrollHeight;
      }
    };

    // The BANK/UNIT filters compare the row's own bank/lift ids, so they apply
    // to outbound rows exactly as they do to inbound ones — a TX row decoded
    // from the wire carries the same UuidT values the controller replies with.
    const passesFilter = (row: TerminalRowInterface): boolean => {
      const filter = filterTypeRef.current;
      if (filter === "UNIT") {
        if (!liftIdRef.current) return false;
        return uuidToString(row.liftId) === liftIdRef.current;
      }
      if (filter === "BANK") {
        if (!bankIdRef.current) return false;
        return uuidToString(row.bankId) === bankIdRef.current;
      }
      return true;
    };

    const enqueue = (row: TerminalRowInterface) => {
      // Cap the staging buffer so a backgrounded tab (RAF throttled to 0Hz)
      // can't grow unbounded between flushes.
      if (pendingMessagesRef.current.length >= MAX_TERMINAL_MESSAGES) {
        pendingMessagesRef.current.shift();
      }
      pendingMessagesRef.current.push(row);
      if (!flushScheduledRef.current) {
        flushScheduledRef.current = true;
        requestAnimationFrame(flush);
      }
    };

    const unsubscribeRx = subscribeWebSocketMessage((msg) => {
      if (stopSocketConnectionRef.current) return;
      if (!passesFilter(msg)) return;
      enqueue(msg);
      if (binaryCaptureActiveRef.current) {
        capturedMessagesRef.current.push({
          timestamp: Date.now(),
          message: msg,
        });
      }
    });

    const unsubscribeTx = subscribeUiCmdTx((event) => {
      if (!terminalShowTxMessagesRef.current) return;
      // Decode the bytes that were sent. A frame that does not parse is
      // dropped rather than shown half-populated — an operator reading the
      // Terminal is reading it as evidence.
      const row = txFrameToTerminalRow(event);
      if (!row) return;
      if (!passesFilter(row)) return;
      enqueue(row);
      // Deliberately NOT added to capturedMessagesRef: a .ln2r capture is a
      // recording of inbound controller traffic for replay, and splicing our
      // own outbound commands into it would make the replay describe a session
      // that never happened.
    });

    return () => {
      unmountedRef.current = true;
      unsubscribeRx();
      unsubscribeTx();
      pendingMessagesRef.current = [];
    };
  }, [setSocketMessages, capturedMessagesRef]);

  useEffect(() => {
    if (terminalDownloadReport) {
      // Generate excel data
      const copy = structuredClone(socketMessages);
      const newArr: TerminalExcelReportInterface[] = [];
      copy.map((message: SocketMessageInterface) => {
        newArr.push({
          bank_id: uuidToString(message.bankId),
          lift_id: uuidToString(message.liftId),
          position: message.position !== null && message.position >= 0 ? message.position : 0,
          direction: directionDisplayName(message.direction),
          msg_type_value: Number(message.msg?.messageType ?? 0),
          content_type_value: Number(message.msg?.contentType ?? 0),
          msg_type: String(message.msg?.messageType ?? ""),
          msg_value: String(message.msg?.contentType ?? ""),
          msg_active: message.msg?.active ?? false,
          faults: message.faults ?? false,
          active_faults:
            message.activeFaults?.length > 0
              ? message.activeFaults
                  .map((f) => getFaultDisplayName(f.faultType, f.fault?.fault?.message ?? 0))
                  .join("; ")
              : "",
          service_mode: (() => {
            const info = getServiceModeDisplay(
              message.serviceMode?.messageType ?? null,
              message.serviceMode?.message?.message?.message ?? null,
            );
            return info?.displayName ?? "";
          })(),
          connected: message.connected ?? false,
          door_status_front: message.doorStatus?.frontOpen ?? false,
          door_status_rear: message.doorStatus?.rearOpen ?? false,
          car_calls: message.carCalls ? JSON.stringify(message.carCalls).replaceAll(",", "-") : "",
          hall_calls: message.hallCalls
            ? JSON.stringify(message.hallCalls).replaceAll(",", "-")
            : "",
          display_map: message.bitmask ? JSON.stringify(message.bitmask).replaceAll(",", "-") : "",
        });
        return true;
      });
      //
      generateExcelAndDownload(newArr, "replayFile");
    }
  }, [terminalDownloadReport]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div ref={tableContainerRef}>
      <Table
        rowKey={(record) => coalesceKey(record as TerminalRowInterface)}
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        dataSource={socketMessages}
        columns={columns.filter((col) => col.show !== false)}
        scroll={{ y: 340 }}
        size="small"
        pagination={{
          current: 1,
          pageSize: 999999,
          showSizeChanger: false,
        }}
        style={{
          height: 400,
          cursor: "pointer",
        }}
        className="my-custom-table"
        onRow={(record: TerminalRowInterface) => ({
          onClick: () => {
            if (stopSocketConnection) {
              // INSPECT_MESSAGE, not UPDATE_MESSAGE: the click should put this
              // one historical record in front of the operator, not fold it
              // into byLift and the monotonic CDO maps as though it had just
              // arrived off the wire. See the INSPECT_MESSAGE case in
              // BankMessagesContext for why that distinction matters — in
              // split view the dashboard pane is showing the same state.
              dispatchSocketMessage({
                type: "INSPECT_MESSAGE",
                payload: record,
              });
            } else {
              navigator.clipboard
                .writeText(stringifyRow(record))
                .then(() => launchNotification())
                .catch(() =>
                  notification.error({
                    message: "Error",
                    placement: "top",
                    className: `monitoring__notification__${theme}`,
                  }),
                );
            }
          },
        })}
      />
    </div>
  );
}
